import i18n from 'i18next'
import type { Resource } from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { SUPPORTED, DEFAULT_LANG, isLang, type Lang } from './config/locales'

// Translations are loaded as per-locale JSON chunks BEFORE the app renders.
//
// History: the site is prerendered (SSG) and the client HYDRATES that markup.
// The old `i18next-http-backend` fetched translations AFTER the bundle ran, so
// react-i18next was "not ready" at hydrateRoot time and every useTranslation()
// suspended to a spinner - client-first-render ≠ server HTML → hydration
// mismatch, React error #418, on EVERY prerendered route. The first fix bundled
// every locale eagerly into the main chunk; that was correct but, once the
// config content migrated into per-locale JSON namespaces, it ballooned the
// entry chunk (~400KB → ~900KB gz) with 10 locales a visitor never reads.
//
// Current model - both properties at once:
//   - main.tsx AWAITS initI18n() before hydrate/render, so by first render the
//     ACTIVE locale (path-derived) + the Danish fallback are fully in memory:
//     hydration is still byte-identical to the prerender, nothing suspends.
//   - Every <lang>/<ns>.json is its own lazy chunk (non-eager import.meta.glob);
//     the other locales load on demand in loadLocale() when the user switches
//     language (LangLayout awaits it before i18n.changeLanguage).
//
// The prerender does NOT use this module: scripts/prerender.mjs reads the same
// JSON files off disk and renders through isolated instances (i18n.server.ts).
// Tests initialise the same singleton in src/test/setup.ts (eager glob, da+en).
const loaders = import.meta.glob('../public/locales/*/*.json') as Record<
  string,
  () => Promise<{ default: Record<string, unknown> }>
>

function pathParts(path: string): { lng: string; ns: string } {
  const segments = path.split('/')
  return {
    lng: segments[segments.length - 2],
    ns: segments[segments.length - 1].replace(/\.json$/, ''),
  }
}

// Namespace set is derivable from file names alone - no loading needed.
const ALL_NS = [...new Set(Object.keys(loaders).map((p) => pathParts(p).ns))]

const loadedLangs = new Set<string>()

async function fetchLang(lng: Lang): Promise<Record<string, Record<string, unknown>>> {
  const entries = Object.entries(loaders).filter(([p]) => pathParts(p).lng === lng)
  const bundles: Record<string, Record<string, unknown>> = {}
  await Promise.all(
    entries.map(async ([p, load]) => {
      bundles[pathParts(p).ns] = (await load()).default
    }),
  )
  return bundles
}

/**
 * Ensures a locale's namespaces are registered on the singleton. Idempotent.
 * Call (and await) before i18n.changeLanguage(lng) - see LangLayout.
 */
export async function loadLocale(lng: Lang): Promise<void> {
  if (loadedLangs.has(lng)) return
  const bundles = await fetchLang(lng)
  for (const [ns, data] of Object.entries(bundles)) {
    i18n.addResourceBundle(lng, ns, data, true, true)
  }
  loadedLangs.add(lng)
}

/** Locale from the URL path (segment 0), mirroring the path detector. */
function activeLang(): Lang {
  const seg = window.location.pathname.split('/').filter(Boolean)[0] ?? ''
  return isLang(seg) ? seg : DEFAULT_LANG
}

/**
 * Loads the active locale (+ Danish fallback) and initialises the singleton.
 * main.tsx awaits this before hydrateRoot/createRoot - the one ordering that
 * keeps hydration byte-identical without bundling all 11 locales eagerly.
 */
export async function initI18n(): Promise<typeof i18n> {
  const active = activeLang()
  const langs: Lang[] = active === DEFAULT_LANG ? [DEFAULT_LANG] : [active, DEFAULT_LANG]
  const resources: Record<string, Record<string, unknown>> = {}
  await Promise.all(
    langs.map(async (lng) => {
      resources[lng] = await fetchLang(lng)
      loadedLangs.add(lng)
    }),
  )

  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: DEFAULT_LANG,
      supportedLngs: SUPPORTED,
      ns: ALL_NS,
      defaultNS: 'translation',
      resources: resources as Resource,
      detection: {
        order: ['path'],
        lookupFromPathIndex: 0,
      },
      interpolation: {
        escapeValue: false,
      },
      // Resources for the active locale are in memory before the first render →
      // `ready` is synchronously true and nothing suspends. Belt-and-suspenders
      // against any not-ready code path.
      react: {
        useSuspense: false,
      },
    })
  return i18n
}

export default i18n
